import { useEffect, useRef, useState } from "react"
import DailyIframe, { type DailyInputSettings } from "@daily-co/daily-js"
import { ImagePlus } from "lucide-react"
import { changeVideoEffect, useVideoEffects } from "./videoEffects"

export type BackgroundEffectChoice = "none" | "soft-blur" | "strong-blur" | "image"

/** Blur strengths behind Prebuilt's "Soft blur" and "Strong blur" choices (Daily's scale is 0–1). */
export const SOFT_BLUR_STRENGTH = 0.5
export const STRONG_BLUR_STRENGTH = 1

export const readBackgroundEffect = (inputSettings: DailyInputSettings | null | undefined): BackgroundEffectChoice => {
  const processor = inputSettings?.video?.processor
  if (!processor || processor.type === "none") return "none"
  if (processor.type === "background-blur") return (processor.config?.strength ?? SOFT_BLUR_STRENGTH) >= STRONG_BLUR_STRENGTH ? "strong-blur" : "soft-blur"
  if (processor.type === "background-image") return "image"
  return "none"
}

/** Daily's video processors need a browser with WebGL/WASM video processing support. */
export const supportsBackgroundEffects = () => {
  try {
    const supported = (DailyIframe as any)?.supportedBrowser?.()
    return supported?.supportsVideoProcessing !== false
  } catch {
    return true
  }
}

const CHOICES: Array<{ id: BackgroundEffectChoice; label: string }> = [
  { id: "none", label: "None" },
  { id: "soft-blur", label: "Soft blur" },
  { id: "strong-blur", label: "Strong blur" },
]

/**
 * Prebuilt's Background effects settings: none, soft blur, strong blur, or a custom image,
 * applied through Daily's input settings so they travel with the camera track.
 */
export function BackgroundEffects() {
  const effects = useVideoEffects()
  const [readingImage, setReadingImage] = useState(false)
  const [imageName, setImageName] = useState<string | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)
  const fileGenerationRef = useRef(0)
  useEffect(() => () => { fileGenerationRef.current += 1 }, [])
  const current = readBackgroundEffect(effects.desired ? { video: { processor: effects.desired } } : effects.inputSettings)
  const pending = effects.pending !== null || readingImage
  const supported = supportsBackgroundEffects()

  const apply = async (choice: BackgroundEffectChoice, source?: ArrayBuffer) => {
    fileGenerationRef.current += 1
    setReadingImage(false)
    setFileError(null)
    if (!effects.call) return false
    if (choice === "none") return changeVideoEffect(effects.call, { type: "none" })
    if (choice === "image" && source) return changeVideoEffect(effects.call, { type: "background-image", config: { source } })
    if (choice !== "image") return changeVideoEffect(effects.call, { type: "background-blur", config: { strength: choice === "strong-blur" ? STRONG_BLUR_STRENGTH : SOFT_BLUR_STRENGTH } })
    return false
  }

  const handleFile = async (file: File | undefined) => {
    if (!file) return
    const supportedType = file.type ? ["image/jpeg", "image/png"].includes(file.type.toLowerCase()) : /\.(jpe?g|png)$/i.test(file.name)
    if (!supportedType) {
      setFileError("Choose a JPEG (.jpg or .jpeg) or PNG (.png) image.")
      return
    }
    setFileError(null)
    setReadingImage(true)
    const generation = ++fileGenerationRef.current
    try {
      const source = await file.arrayBuffer()
      if (generation !== fileGenerationRef.current) return
      setReadingImage(false)
      if (await apply("image", source)) setImageName(file.name)
    } catch {
      if (generation === fileGenerationRef.current) setFileError("Could not apply this background. Try another JPEG or PNG image.")
    } finally {
      if (generation === fileGenerationRef.current) setReadingImage(false)
    }
  }

  return (
    <div data-testid="call-background-effects" className="rounded-xl border border-white/10 bg-white/[0.03] p-3 sm:p-4">
      <span className="mb-3 block text-sm font-semibold text-white">Background effects</span>
      {!supported ? (
        <p className="text-xs leading-relaxed text-white/60">This browser cannot apply background effects.</p>
      ) : (
        <div role="radiogroup" aria-label="Background effects" className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {CHOICES.map((choice) => (
            <button
              key={choice.id}
              type="button"
              role="radio"
              aria-checked={current === choice.id}
              disabled={pending && (choice.id !== "none" || effects.desired?.type === "none")}
              onClick={() => void apply(choice.id)}
              className={`flex min-h-11 min-w-0 items-center justify-between gap-2 rounded-xl border px-3 py-3 text-left text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6a35c] focus-visible:ring-offset-2 focus-visible:ring-offset-[#151a1f] disabled:cursor-wait disabled:opacity-50 ${current === choice.id ? "border-[#f6a35c]/70 bg-[#f6a35c]/10 text-white" : "border-white/10 bg-white/5 text-white/80 hover:border-white/25 hover:bg-white/10"}`}
            >
              {choice.label}
              <span aria-hidden="true" className={`h-3 w-3 shrink-0 rounded-full border-2 ${current === choice.id ? "border-[#f6a35c] bg-[#f6a35c]" : "border-white/30"}`} />
            </button>
          ))}
          <button
            type="button"
            role="radio"
            aria-checked={current === "image"}
            disabled={pending}
            onClick={() => fileRef.current?.click()}
            className={`col-span-full flex min-h-11 min-w-0 items-center gap-2.5 rounded-xl border px-3 py-3 text-left text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6a35c] focus-visible:ring-offset-2 focus-visible:ring-offset-[#151a1f] disabled:cursor-wait disabled:opacity-50 ${current === "image" ? "border-[#f6a35c]/70 bg-[#f6a35c]/10 text-white" : "border-dashed border-white/20 bg-white/5 text-white/80 hover:border-white/40 hover:bg-white/10"}`}
          >
            <ImagePlus aria-hidden="true" className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{current === "image" && imageName ? imageName : "Use custom background"}</span>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
            aria-label="Choose a background image"
            data-testid="call-background-image-input"
            className="sr-only"
            disabled={pending}
            onChange={(event) => {
              const file = event.target.files?.[0]
              event.target.value = ""
              void handleFile(file)
            }}
          />
        </div>
      )}
      {supported && <p className="mt-3 text-xs leading-relaxed text-white/50">JPEG or PNG images only.</p>}
      {effects.waitingForVisibility && <p role="status" className="mt-3 text-xs leading-relaxed text-white/70">Return to this tab to apply your background.</p>}
      {(fileError || effects.error) && <p role="status" className="mt-3 rounded-lg border border-amber-200/20 bg-amber-200/5 px-3 py-2.5 text-xs leading-relaxed text-amber-200">{fileError || effects.error}</p>}
    </div>
  )
}
